import React from 'react';
import { View } from 'react-native';

import Reveal from '@/components/Landing/Reveal';
import {
  BleedRule,
  DisplayHeading,
  Figure,
  Lede,
  Section,
  useLanding,
} from '@/components/Landing/LandingKit';
import { fluid } from '@/components/Landing/landingTokens';
import {
  Card,
  CheckLine,
  IconTile,
  RuledGrid,
  SectionIntro,
} from '@/components/Landing/MarketingKit';
import { ENGAGEMENT_FIGURES, SERVICES } from '@/constants/enterpriseContent';

/** Who we are, then the six things an engagement is usually made of. */
function EnterpriseServices() {
  const { isXS, isCompact, width, display } = useLanding();

  const h3 = display({ min: 19, max: 24, leading: 1.16, tracking: -0.026 });

  return (
    <View style={{ width: '100%', alignSelf: 'stretch', alignItems: 'center' }}>

      {/* ============ WHO YOU'RE HIRING ============ */}
      <Section label="Why Gab AI builds enterprise AI">
        <SectionIntro
          kicker="Who you're hiring"
          lines={[['Engineers who have'], ['already shipped this.']]}
          lede="We are not consultants who hand you a slide deck. Our team has spent over a decade building and operating services used by tens of millions of people, and brings that same engineering to every AI system we deliver."
          ledeMax={600}
        />

        <Reveal delay={100}>
          <RuledGrid
            items={ENGAGEMENT_FIGURES}
            keyOf={(figure) => figure.label}
            edges="both"
            style={{ marginTop: fluid(width, 40, 62) }}
            renderItem={(figure, index) => (
              <Figure
                value={figure.value}
                label={figure.label}
                size="sm"
                accent={index === 0}
              />
            )}
          />
        </Reveal>
      </Section>

      <BleedRule />

      {/* ============ WHAT WE BUILD ============ */}
      <Section label="Enterprise AI services">
        <SectionIntro
          kicker="What we build"
          lines={[['Six kinds of work,'], ['one engagement.']]}
          lede="Most projects draw on several of these at once. We scope them together rather than selling them as separate products."
          ledeMax={580}
        />

        <Reveal delay={100}>
          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: isXS ? 12 : 16,
              marginTop: fluid(width, 36, 56),
            }}
          >
            {SERVICES.map((service) => (
              <Card
                key={service.title}
                style={{
                  flexGrow: 1,
                  flexBasis: isCompact ? '100%' : '30%',
                  minWidth: 0,
                  gap: 18,
                }}
              >
                <IconTile name={service.icon} size={42} />
                <View>
                  <DisplayHeading tag="h3" mask={false} type={h3} lines={[[service.title]]} />
                  <Lede size="sm" style={{ marginTop: 10 }}>{service.copy}</Lede>
                </View>
                <View style={{ gap: 9, marginTop: 'auto' }}>
                  {service.points.map((point) => (
                    <CheckLine key={point} size={13}>{point}</CheckLine>
                  ))}
                </View>
              </Card>
            ))}
          </View>
        </Reveal>
      </Section>
    </View>
  );
}

export default React.memo(EnterpriseServices);
